declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (command: "event", eventName: string, params?: Record<string, unknown>) => void;
  }
}

/**
 * A form lifecycle event. The key names are the contract: orgs build GTM triggers and GA4 custom
 * dimensions against `formId`/`fieldId`/`title`, so a rename here silently breaks tags they have
 * already configured against the legacy payload.
 */
export interface FormProgressEvent {
  event: "form_progress" | "form_submitted";
  formId: string;
  fieldId: string;
  title: string;
  previousTitle: string;
  status: string;
}

/**
 * A completed Stripe charge. A separate shape from {@link FormProgressEvent} rather than a variant
 * of it — the two share no keys beyond `fieldId`, and the names here are equally load-bearing for
 * orgs' existing purchase tags. `value` and `purchaseCost` are the same figure in dollars, as
 * legacy sent both.
 */
export interface FormPurchaseEvent {
  event: "form_purchase";
  productIds: string[];
  fieldId: string;
  value: number;
  currency: string;
  purchaseName: string;
  purchaseCost: number;
}

export type FormAnalyticsEvent = FormProgressEvent | FormPurchaseEvent;

/** GA4's own framing of the same event, which legacy sent separately via `ReactGA.event`. */
interface Ga4EventParams {
  /** `form_<formId>`, as legacy's `category`. */
  category: string;
  /** "Form Progress" / "Form Submitted", as legacy's `action`. */
  action: string;
  label: string;
  value: number;
}

/**
 * Pushes a form event to GTM and GA4.
 *
 * Both are separate destinations, not one: a `dataLayer` push is a **GTM** custom event, which GA4
 * only ever receives if the org happens to have wired a forwarding tag. Legacy therefore sent each
 * its own event (`emit_gtm_event` alongside `ReactGA.event`), and both are reproduced here against
 * the globals `useFormAnalytics` has already injected — a `react-ga4` dependency would re-inject the
 * gtag script and re-fire `gtag('config', …)`, double-counting pageviews.
 *
 * A missing global means the form configured no such tag, which is the common case: the send is
 * skipped rather than creating `dataLayer`, so nothing accumulates unread for an untracked form.
 * Analytics must never break form filling, so failures are swallowed.
 *
 * `ga4` is omitted for GTM-only events — legacy sent the purchase through `emit_gtm_event` alone,
 * with no `ReactGA.event` beside it, so passing one would invent an event orgs never received.
 */
export const emitFormAnalyticsEvent = (event: FormAnalyticsEvent, ga4?: Ga4EventParams): void => {
  try {
    window.dataLayer?.push(event);
  } catch {
    // A tag manager can replace `dataLayer` with a proxy that throws on push.
  }

  if (!ga4) return;

  try {
    // GA4's reserved `value` needs a currency alongside it to be reportable; legacy sent it bare, so
    // it lands as a plain number here too rather than inventing a currency the org never configured.
    window.gtag?.("event", ga4.action, {
      event_category: ga4.category,
      event_label: ga4.label,
      value: ga4.value,
      transport_type: "beacon",
    });
  } catch {
    // Same rationale as above — the gtag shim is third-party code.
  }
};
